import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import PageHeader from '../components/PageHeader'
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline'
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin } from 'react-icons/fa'

const contactInfo = [
  {
    name: 'Email',
    description: 'paciehng@gmail.com',
    icon: EnvelopeIcon,
    href: 'mailto:paciehng@gmail.com',
  },
  {
    name: 'Phone Numbers',
    description: '+234 803 272 9271 / 08035499868',
    icon: PhoneIcon,
    href: 'tel:+2348032729271',
  },
  {
    name: 'Clinical Facility Location',
    description: 'Goodwill Medical Centre, Umuchigbo, Enugu East LGA, Enugu State, Nigeria',
    icon: MapPinIcon,
    href: '#map',
  },
]

const socialLinks = [
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/people/Pan-African-Community-Initiative-on-Education-and-Health-Pacieh/100064950510004/',
    icon: FaFacebook,
  },
  {
    name: 'Twitter',
    href: 'https://x.com/paciehnigeria',
    icon: FaTwitter,
  },
  {
    name: 'Instagram',
    href: '#',
    icon: FaInstagram,
  },
  {
    name: 'LinkedIn',
    href: '#',
    icon: FaLinkedin,
  },
]

const contactSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Name is too short')
    .max(50, 'Name is too long')
    .required('Name is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  subject: Yup.string()
    .min(2, 'Subject is too short')
    .max(100, 'Subject is too long')
    .required('Subject is required'),
  message: Yup.string()
    .min(10, 'Message is too short')
    .max(1000, 'Message is too long')
    .required('Message is required'),
})

function Contact() {
  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    // Redirect to Google Form
    window.open('https://forms.gle/GEWGVePjmHVZJKzd9', '_blank');
    setSubmitting(false);
    resetForm();
  }

  return (
    <>
      <Helmet>
        <title>Contact Us | PACIEH</title>
        <meta name="description" content="Get in touch with PACIEH. Contact us for inquiries about our programs, partnerships, or how you can support our mission." />
      </Helmet>

      <PageHeader
        title="Contact Us"
        subtitle="Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible."
      />

      <section className="py-24 bg-white">
        <div className="container">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl font-bold text-pacieh-blue mb-8">Send us a message</h2>
              <Formik
                initialValues={{
                  name: '',
                  email: '',
                  subject: '',
                  message: '',
                }}
                validationSchema={contactSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Name
                      </label>
                      <Field
                        type="text"
                        name="name"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pacieh-green focus:ring-pacieh-green"
                      />
                      <ErrorMessage name="name" component="div" className="mt-1 text-red-600 text-sm" />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <Field
                        type="email"
                        name="email"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pacieh-green focus:ring-pacieh-green"
                      />
                      <ErrorMessage name="email" component="div" className="mt-1 text-red-600 text-sm" />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                        Subject
                      </label>
                      <Field
                        type="text"
                        name="subject"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pacieh-green focus:ring-pacieh-green"
                      />
                      <ErrorMessage name="subject" component="div" className="mt-1 text-red-600 text-sm" />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                        Message
                      </label>
                      <Field
                        as="textarea"
                        name="message"
                        rows={4}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pacieh-green focus:ring-pacieh-green"
                      />
                      <ErrorMessage name="message" component="div" className="mt-1 text-red-600 text-sm" />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </Form>
                )}
              </Formik>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="space-y-12"
            >
              <div>
                <h2 className="text-2xl font-bold text-pacieh-blue mb-8">Get in touch</h2>
                <dl className="space-y-6">
                  {contactInfo.map((item) => (
                    <div key={item.name} className="flex gap-4">
                      <dt>
                        <item.icon className="h-6 w-6 text-pacieh-green" aria-hidden="true" />
                      </dt>
                      <dd>
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <a href={item.href} className="text-gray-600 hover:text-pacieh-green">
                          {item.description}
                        </a>
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-pacieh-blue mb-8">Follow us</h2>
                <div className="flex space-x-6">
                  {socialLinks.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="text-gray-500 hover:text-pacieh-green"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="sr-only">{item.name}</span>
                      <item.icon className="h-6 w-6" aria-hidden="true" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Map Embed */}
              <div id="map" className="aspect-[4/3] bg-gray-100 rounded-2xl overflow-hidden shadow-md border border-gray-200">
                <iframe
                  src="https://maps.google.com/maps?q=Goodwill+Medical+Centre+Enugu+Nigeria&t=&z=13&ie=UTF8&iwloc=&output=embed"
                  title="Goodwill Medical Centre Location"
                  className="w-full h-full border-0"
                  allowFullScreen=""
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Contact 